import { useEffect } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  Package,
  Truck,
  Banknote,
  RotateCcw,
  Users,
  AlertTriangle,
  ShoppingBag,
  Receipt,
  Search,
  ChevronRight,
} from 'lucide-react';
import { PageLayout } from '@/components';
import { useGlobalSearch } from '@/hooksApi/useGlobalSearch';
import type { GlobalSearchResult, SearchCategory } from '@/types/globalSearch';

const CATEGORY_META: Record<SearchCategory, { icon: typeof Package; labelKey: string }> = {
  order: { icon: Package, labelKey: 'orders' },
  pickup: { icon: Truck, labelKey: 'pickups' },
  remittance: { icon: Banknote, labelKey: 'remittances' },
  rto: { icon: RotateCcw, labelKey: 'rto' },
  contact: { icon: Users, labelKey: 'contacts' },
  claim: { icon: AlertTriangle, labelKey: 'claims' },
  product: { icon: ShoppingBag, labelKey: 'product_catalog' },
  transaction: { icon: Receipt, labelKey: 'transactions' },
};

// Import from single source of truth — covers ALL statuses
import { STATUS_HSL as STATUS_COLORS } from '@/pages/order-details/components/StatusColors';

function Highlight({ text, query }: { text: string; query: string }) {
  if (!query || !text) return <>{text}</>;
  const idx = text.toLowerCase().indexOf(query.toLowerCase());
  if (idx === -1) return <>{text}</>;
  return (
    <>
      {text.slice(0, idx)}
      <span className='bg-[#EBDD4C]/30 rounded-sm px-0.5'>
        {text.slice(idx, idx + query.length)}
      </span>
      {text.slice(idx + query.length)}
    </>
  );
}

const SearchResultsPage = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const query = searchParams.get('q') || '';

  const { groups, isLoading, totalResults } = useGlobalSearch(query, true);

  useEffect(() => {
    if (!query) navigate('/dashboard', { replace: true });
  }, [query, navigate]);

  if (!query) return null;

  return (
    <PageLayout>
      <div className='sd-scope sd-font'>
        {/* Header */}
        <div className='mb-6'>
          <h1 className='text-xl font-bold text-sd-foreground m-0'>
            {String(t('search_results', { defaultValue: 'Search Results' }))}
          </h1>
          <p className='text-sm text-sd-muted-foreground mt-1 m-0'>
            {isLoading
              ? String(t('searching', { defaultValue: 'Searching...' }))
              : `${totalResults} ${String(t('results_for', { defaultValue: 'results for' }))} "${query}"`}
          </p>
        </div>

        {/* Loading */}
        {isLoading && (
          <div className='flex items-center justify-center py-16'>
            <div className='h-6 w-6 animate-spin rounded-full border-2 border-sd-primary border-t-transparent' />
          </div>
        )}

        {/* No results */}
        {!isLoading && totalResults === 0 && (
          <div className='flex flex-col items-center justify-center py-16'>
            <Search className='h-12 w-12 text-sd-muted-foreground/40 mb-3' />
            <p className='text-[15px] font-semibold text-sd-foreground'>
              {String(t('no_results_found'))}
            </p>
            <p className='text-sm text-sd-muted-foreground mt-1'>
              {String(t('no_results_found_hint', { defaultValue: 'Try a different search term' }))}
            </p>
          </div>
        )}

        {/* Results grouped by category */}
        {!isLoading && groups.map((group) => {
          const meta = CATEGORY_META[group.category];
          if (!meta) return null;
          const Icon = meta.icon;

          return (
            <div key={group.category} className='mb-6'>
              {/* Category header */}
              <div className='flex items-center gap-2 mb-3 px-1'>
                <Icon className='h-4 w-4 text-sd-muted-foreground' />
                <h2 className='text-[13px] font-bold uppercase tracking-wider text-sd-muted-foreground m-0'>
                  {String(t(meta.labelKey))}
                </h2>
                <span className='text-[11px] text-sd-muted-foreground/70'>
                  ({group.total})
                </span>
              </div>

              {/* Result cards */}
              <div className='rounded-xl border border-sd-border bg-sd-card overflow-hidden divide-y divide-sd-border'>
                {group.results.map((result: GlobalSearchResult) => {
                  const statusStyle = result.statusSlug ? STATUS_COLORS[result.statusSlug] : null;

                  return (
                    <button
                      key={`${result.category}-${result.id}`}
                      onClick={() => navigate(result.href)}
                      className='flex w-full items-center gap-3 px-4 py-3 text-start transition-colors hover:bg-sd-muted/50 bg-transparent border-0 cursor-pointer'
                    >
                      <div className='flex-1 min-w-0'>
                        <div className='flex items-center gap-2'>
                          <span className='text-[13px] font-semibold text-sd-foreground'>
                            <Highlight text={result.primary} query={query} />
                          </span>
                          {result.tag && (
                            <span className='text-[11px] px-1.5 py-0.5 rounded bg-sd-muted text-sd-foreground'>
                              <Highlight text={result.tag} query={query} />
                            </span>
                          )}
                        </div>
                        {result.secondary && (
                          <p className='text-[12px] text-sd-muted-foreground mt-0.5 m-0 truncate'>
                            <Highlight text={result.secondary} query={query} />
                          </p>
                        )}
                      </div>
                      {result.statusText && statusStyle && (
                        <span
                          className='shrink-0 rounded-full px-2.5 py-1 text-[11px] font-semibold'
                          style={{ background: statusStyle.bg, color: statusStyle.text }}
                        >
                          {result.statusText}
                        </span>
                      )}
                      <ChevronRight className='h-4 w-4 text-sd-muted-foreground shrink-0' />
                    </button>
                  );
                })}
              </div>
            </div>
          );
        })}
      </div>
    </PageLayout>
  );
};

export default SearchResultsPage;
